import { useState, useEffect, useCallback } from "react";
import sequenceApis from "../api/master/sequence";

interface UseSequenceNumberingOptions {
  moduleName: string;
  setValue: (name: string, value: any) => void;
  getValues?: (name: string) => any;
  fieldName?: string;
  isEdit?: boolean;
  id?: string | null;
  persistModeToForm?: boolean;
}

export default function useSequenceNumbering({
  moduleName,
  setValue,
  getValues,
  fieldName = "referenceID",
  isEdit = false,
  id,
  persistModeToForm = false,
}: UseSequenceNumberingOptions) {
  const [activeSequences, setActiveSequences] = useState<any[]>([]);
  const [numberingMode, setNumberingMode] = useState<"auto" | "manual">("auto");
  const [showNumberingModal, setShowNumberingModal] = useState(false);
  const [tempNumberingMode, setTempNumberingMode] = useState<"auto" | "manual">("auto");

  const getActiveSequence = useCallback(() => {
    if (activeSequences.length === 0) return null;
    const orgSequence = activeSequences.find((seq: any) => seq.level === "Org");
    return orgSequence || activeSequences[0];
  }, [activeSequences]);

  const currentSequence = getActiveSequence();
  const canAuto = activeSequences.length > 0;

  const fetchSequenceAccess = useCallback(async () => {
    try {
      const response = await sequenceApis.fetchSequence();
      const { data } = response;
      const today = new Date();
      const _sequenceAccess = data?.filter(
        (item: any) =>
          item.moduleName === moduleName &&
          new Date(item.endDate) > today &&
          item.status === "Active"
      );
      setActiveSequences(_sequenceAccess || []);

      // For multi-step forms, check if mode is already persisted in form
      if (persistModeToForm && getValues) {
        const existingMode = getValues("numberingMode");
        if (existingMode) {
          setNumberingMode(existingMode);
          return;
        }
      }

      let mode: "auto" | "manual";
      if (_sequenceAccess && _sequenceAccess.length > 0) {
        mode = "auto";
      } else {
        mode = "manual";
      }
      setNumberingMode(mode);

      if (persistModeToForm) {
        setValue("numberingMode", mode);
      }
    } catch (error: any) {
      console.error("Error fetching sequences:", error);
      setActiveSequences([]);
      setNumberingMode("manual");
      if (persistModeToForm) {
        setValue("numberingMode", "manual");
      }
    }
  }, [moduleName, persistModeToForm, getValues, setValue]);

  // Set field value based on numbering mode
  useEffect(() => {
    const currentFieldValue = getValues ? getValues(fieldName) : "";
    if (numberingMode === "auto" && currentSequence && !isEdit && !id) {
      const { prefix } = currentSequence;
      if (!currentFieldValue || currentFieldValue === prefix) {
        setValue(fieldName, prefix);
      }
    } else if (numberingMode === "manual" && !isEdit && !id) {
      const isCurrentlyPrefix = currentSequence && currentFieldValue === currentSequence.prefix;
      if (isCurrentlyPrefix) {
        setValue(fieldName, "");
      }
    }
  }, [numberingMode, currentSequence, isEdit, id, fieldName, setValue, getValues]);

  // Force manual mode when editing
  const setEditMode = useCallback(() => {
    setNumberingMode("manual");
  }, []);

  const openModal = useCallback(() => {
    setTempNumberingMode(numberingMode);
    setShowNumberingModal(true);
  }, [numberingMode]);

  const closeModal = useCallback(() => {
    setShowNumberingModal(false);
  }, []);

  const saveModalSelection = useCallback(() => {
    if (tempNumberingMode === "auto" && !currentSequence) {
      return false; // caller should show error
    }

    setNumberingMode(tempNumberingMode);

    if (persistModeToForm) {
      setValue("numberingMode", tempNumberingMode);
    }

    if (tempNumberingMode === "auto" && currentSequence) {
      const { prefix } = currentSequence;
      setValue(fieldName, prefix);
    } else if (tempNumberingMode === "manual") {
      setValue(fieldName, "");
    }

    setShowNumberingModal(false);
    return true;
  }, [tempNumberingMode, currentSequence, setValue, fieldName, persistModeToForm]);

  // Build payload helper: strips referenceID in auto mode
  const buildPayload = useCallback(
    (data: any) => {
      const { [fieldName]: refId, ...rest } = data;
      const payload: any = { ...rest };
      if (numberingMode === "manual") {
        payload[fieldName] = refId;
      }
      return payload;
    },
    [numberingMode, fieldName]
  );

  return {
    numberingMode,
    setNumberingMode,
    showNumberingModal,
    tempNumberingMode,
    setTempNumberingMode,
    activeSequences,
    currentSequence,
    canAuto,
    fetchSequenceAccess,
    setEditMode,
    openModal,
    closeModal,
    saveModalSelection,
    buildPayload,
  };
}
